import { useContext, useState, useEffect } from "react";
import {
  Alert,
  Backdrop,
  CircularProgress,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import { useNavigate } from "react-router";
import moment from "moment";
import { UserContext } from "../../context/UserContext";
import BDateTimePicker from "../../common/input/BDateTimePicker";
import {
  getDefaultQuoteExpiration,
  hasOwnerAccess,
  isSalesTaxEnabled,
  shouldCollectSalesTax,
} from "../../common/util";
import QuoteFulfillmentEntry from "../../common/component/fulfillment/QuoteFulfillmentEntry";
import LineItems from "../../common/component/lineitem/LineItems";

export default function SendQuote(props) {
  const { user } = useContext(UserContext);
  const { request, quote } = props;

  const navigate = useNavigate();

  const [items, setItems] = useState(props.items || []);
  const [fulfillment, setFulfillment] = useState(props.fulfillment || { type: "pickup" });
  const [exdate, setExdate] = useState("");

  const canToggleSalesTax =
    isSalesTaxEnabled(user.config) &&
    (hasOwnerAccess(user.role) || user.role === "manager");

  const [collectSalesTax, setCollectSalesTax] = useState(() => {
    if (
      quote?.settings?.collectSalesTax !== undefined ||
      quote?.collectSalesTax !== undefined ||
      quote?.settings?.includeSalesTax !== undefined
    ) {
      return shouldCollectSalesTax(quote);
    }
    return isSalesTaxEnabled(user.config);
  });

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fulfillment.date) {
      setExdate(getDefaultQuoteExpiration(fulfillment.date));
    }
  }, [fulfillment.date]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const mode = e.nativeEvent?.submitter?.name === "save" ? "save" : "send";

    let payload = {
      mode,
      items,
      fulfillment,
      exdate,
      settings: { collectSalesTax },
      appVersion: process.env.REACT_APP_VERSION,
    };

    setLoading(true);
    bkstApi
      .put(`/my/quote/${request.id}`, payload)
      .then((res) => {
        navigate(`/quote/${request.id}`);
      })
      .catch((error) => {
        console.log(error);
        window.location.reload();
      });
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="md">
        <Box py={4}>
          <form onSubmit={handleSubmit}>
            <Box>
              <Typography variant="h1" gutterBottom>
                Send Quote
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Add one or more items, set fulfillment options & click button to send your quote
              </Typography>
            </Box>
            <Box my={4}>
              <LineItems items={items} onChange={(arr) => setItems(arr)} />
              {items.length > 0 && (
                <Box>
                  <Box my={4}>
                    <QuoteFulfillmentEntry
                      fulfillment={fulfillment}
                      onChange={(o) => setFulfillment(o)}
                    />
                  </Box>
                  {canToggleSalesTax && (
                    <Box my={4}>
                      <Paper elevation={0}>
                        <Box px={2} py={1}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={collectSalesTax}
                                onChange={(e) => setCollectSalesTax(e.target.checked)}
                              />
                            }
                            label="Collect Sales Tax"
                          />
                          <Typography variant="body2" color="textSecondary">
                            Turn off for wholesale or tax-exempt quotes. When on, sales tax applies
                            only to items marked taxable in the catalog.
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  <Box mt={4} mb={2}>
                    <Box>
                      <FormControl variant="outlined" fullWidth>
                        <BDateTimePicker
                          value={exdate || null}
                          label="Expiration"
                          onChange={(d) => setExdate(d)}
                          disablePast
                          required
                          autoOk
                          maxDateTime={
                            (fulfillment.date && moment(fulfillment.date).endOf("day").toDate()) ||
                            null
                          }
                        />
                      </FormControl>
                    </Box>
                  </Box>
                  {errors && errors.length > 0 && (
                    <Box my={2}>
                      <Alert severity="error">{errors.join(". ")}</Alert>
                    </Box>
                  )}
                </Box>
              )}
              <Box my={4}>
                <Box sx={{ display: "flex", gap: "15px" }}>
                  <Button
                    name="save"
                    color="secondary"
                    fullWidth
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={items.length === 0}
                  >
                    Save Draft
                  </Button>
                  <Button
                    name="send"
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={items.length === 0}
                  >
                    Send Quote
                  </Button>
                </Box>
                <Box my={2} textAlign={"center"}>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => navigate(`/quote/${request.id}`)}
                  >
                    Cancel Changes
                  </Button>
                </Box>
              </Box>
            </Box>
          </form>
        </Box>
      </Container>
      {loading && (
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Box>
  );
}
